import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { Combobox, ComboboxInput } from '@angular/aria/combobox';
import {
    PulseFieldComponent,
    PulsePopupInputDirective,
    PulsePopupValueBase,
    providePulsePopupControl,
} from '../pulse-popup';

/**
 * A searchable single-select. Options go inside a `[pulsePopupOptions]` element, and the consumer
 * filters them by `searchText` — aria never filters options itself.
 *
 * ```html
 * <pulse-combobox [(value)]="unit" [(searchText)]="query" placeholder="Search units">
 *     <div pulsePopupOptions>
 *         @for (unit of shownUnits(); track unit.value) {
 *             <div ngOption [value]="unit.value" [label]="unit.label">
 *                 <pulse-combobox-match [text]="unit.label" [query]="query()" />
 *             </div>
 *         }
 *     </div>
 * </pulse-combobox>
 * ```
 */
@Component({
    selector: 'pulse-combobox',
    exportAs: 'pulseCombobox',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComboboxInput, PulseFieldComponent, PulsePopupInputDirective],
    templateUrl: './pulse-combobox.component.html',
    styleUrl: './pulse-combobox.component.scss',
    hostDirectives: [
        {
            directive: Combobox,
            inputs: ['disabled', 'filterMode'],
        },
    ],
    host: {
        class: 'pulse-combobox',
    },
    providers: [providePulsePopupControl(() => PulseComboboxComponent)],
})
export class PulseComboboxComponent<T> extends PulsePopupValueBase<T> {
    readonly searchText = model('');

    readonly searchable = input(true);

    readonly clearable = input(true);

    readonly clearLabel = input('');

    protected readonly fieldText = computed(() =>
        this.expanded() && this.searchable() ? this.searchText() : this.selectedLabel()
    );

    protected readonly hasValue = computed(() => this.selection().length > 0);

    constructor() {
        super();
        this.combobox._pattern.inputs.readonly = () => !this.searchable();
    }

    protected override onClosed(): void {
        this.searchText.set('');
    }

    protected openField(event: MouseEvent): void {
        // `Combobox.open()` has no disabled guard, and a click on the field's padding never
        // reaches the disabled input that would otherwise swallow it.
        if (this.isDisabled()) {
            return;
        }

        const input = this.inputElement().nativeElement;
        if (!this.searchable()) {
            if (event.target !== input) {
                input.click();
            }
            return;
        }
        input.focus();
        if (!this.expanded()) {
            this.combobox.open();
            return;
        }
        if (event.target !== input) {
            this.combobox.close();
        }
    }

    protected clear(event: MouseEvent): void {
        event.stopPropagation();
        this.searchText.set('');
        this.clearSelection();
        this.inputElement().nativeElement.focus();
    }
}
